import { useState } from "react";
import { testimonials } from "@/mocks/testimonials";

interface TestimonialsProps {
  light?: boolean;
}

const avatarColors = [
  { bg: "bg-[#0EA5E9]/20", text: "text-[#38bdf8]" },
  { bg: "bg-emerald-500/15", text: "text-emerald-400" },
  { bg: "bg-amber-500/15", text: "text-amber-400" },
  { bg: "bg-violet-500/15", text: "text-violet-400" },
  { bg: "bg-rose-500/15", text: "text-rose-400" },
  { bg: "bg-teal-500/15", text: "text-teal-400" },
];

export default function TestimonialsSection({ light }: TestimonialsProps) {
  const [active, setActive] = useState(0);

  const visible = [
    testimonials[active % testimonials.length],
    testimonials[(active + 1) % testimonials.length],
    testimonials[(active + 2) % testimonials.length],
  ];

  const bg = light ? "bg-white" : "bg-[#030c15]";
  const sectionBg = light ? "bg-gray-50" : "bg-[#050F1A]";
  const labelColor = light ? "text-[#0EA5E9]" : "text-[#7DD3FC]";
  const headingColor = light ? "text-gray-900" : "text-white";
  const ratingBg = light ? "bg-amber-50 border-amber-200" : "bg-amber-500/8 border-amber-500/20";
  const ratingText = light ? "text-amber-600" : "text-amber-400";
  const cardBg0 = light
    ? "bg-white border-[#0EA5E9]/40 shadow-md"
    : "bg-gradient-to-br from-[#0f2640] to-[#091a32] border-[#0EA5E9]/40";
  const cardBgRest = light
    ? "bg-white border-gray-100"
    : "bg-[#0d1f30] border-white/12";
  const quoteIcon = light ? "text-[#0EA5E9]/18" : "text-[#0EA5E9]/18";
  const textColor = light ? "text-gray-600" : "text-white/90";
  const divider = light ? "border-gray-100" : "border-white/15";
  const authorName = light ? "text-gray-900" : "text-white";
  const authorSub = light ? "text-gray-400" : "text-white/65";
  const navBtn = light
    ? "border-gray-200 text-gray-400 hover:border-[#0EA5E9] hover:text-[#0EA5E9]"
    : "border-white/20 text-white/55 hover:border-[#0EA5E9]/70 hover:text-[#0EA5E9]";
  const dotActive = "bg-[#0EA5E9] w-6";
  const dotInactive = light ? "bg-gray-200 w-2 hover:bg-gray-300" : "bg-white/30 w-2 hover:bg-white/55";

  return (
    <section className={`${sectionBg} py-28`}>
      <div className="max-w-7xl mx-auto px-6">

        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6">
          <div>
            <p className={`font-['Inter'] ${labelColor} text-xs font-semibold uppercase tracking-[0.18em] mb-4`}>
              Client Stories
            </p>
            <h2 className={`font-['Playfair_Display'] ${headingColor} text-4xl md:text-5xl font-bold leading-tight`}>
              What Our Customers Say
            </h2>
          </div>
          <div className={`inline-flex items-center gap-2.5 border rounded-full px-5 py-2.5 flex-shrink-0 ${ratingBg}`}>
            <div className="flex items-center gap-0.5">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-star-fill ${ratingText} text-xs`}></i>
                </div>
              ))}
            </div>
            <span className={`font-['Inter'] ${ratingText} text-xs font-semibold whitespace-nowrap`}>5.0 · Verified Reviews</span>
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
          {visible.map((t, idx) => {
            const color = avatarColors[t.id % avatarColors.length];
            const isFeatured = idx === 0;
            return (
              <div
                key={`${t.id}-${idx}`}
                className={`relative border rounded-2xl p-8 flex flex-col transition-all duration-400 overflow-hidden ${
                  isFeatured ? cardBg0 : cardBgRest
                }`}
              >
                {/* Decorative accent on featured card */}
                {isFeatured && !light && (
                  <>
                    <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#0EA5E9]/80 via-[#38bdf8]/40 to-transparent" />
                    <div className="absolute top-4 right-4 w-24 h-24 rounded-full bg-[#0EA5E9]/4 blur-2xl pointer-events-none" />
                  </>
                )}

                {/* Stars */}
                <div className="flex items-center gap-0.5 mb-5">
                  {[...Array(t.rating)].map((_, i) => (
                    <div key={i} className="w-[18px] h-[18px] flex items-center justify-center">
                      <i className="ri-star-fill text-[12px] text-amber-400"></i>
                    </div>
                  ))}
                </div>

                {/* Large open-quote mark */}
                <div className={`w-8 h-6 flex items-start mb-3 ${quoteIcon}`}>
                  <i className="ri-double-quotes-l text-5xl leading-none"></i>
                </div>

                {/* Review text */}
                <p className={`font-['Inter'] ${textColor} text-sm leading-[1.92] flex-1 mb-7`}>
                  &ldquo;{t.text}&rdquo;
                </p>

                {/* Author */}
                <div className={`flex items-center gap-3.5 pt-5 border-t ${divider}`}>
                  {/* Avatar with initial */}
                  <div className={`w-11 h-11 flex items-center justify-center rounded-full ${color.bg} flex-shrink-0`}>
                    <span className={`font-['Inter'] ${color.text} text-base font-bold`}>
                      {t.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <div className={`font-['Inter'] ${authorName} font-bold text-sm mb-0.5`}>{t.name}</div>
                    <div className={`font-['Inter'] ${authorSub} text-[11.5px]`}>
                      {t.location} &bull; {t.date}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={() => setActive((p) => (p - 1 + testimonials.length) % testimonials.length)}
            className={`w-10 h-10 flex items-center justify-center rounded-full border transition-all duration-300 cursor-pointer ${navBtn}`}
          >
            <i className="ri-arrow-left-line text-sm"></i>
          </button>
          {testimonials.map((_, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${i === active % testimonials.length ? dotActive : dotInactive}`}
            />
          ))}
          <button
            onClick={() => setActive((p) => (p + 1) % testimonials.length)}
            className={`w-10 h-10 flex items-center justify-center rounded-full border transition-all duration-300 cursor-pointer ${navBtn}`}
          >
            <i className="ri-arrow-right-line text-sm"></i>
          </button>
        </div>
      </div>
    </section>
  );
}
